import React, { useState, useMemo } from "react";
import _, { set } from "lodash";
import { get } from "_shared/request";
import { user } from "../../_shared/auth";
import { queryCache } from "react-query";
import {
  useAddSection,
  useAddResource,
} from "./ResourcesAPI/Resources.mutations";
import { useToasts } from "react-toast-notifications";
import FileIcon from "_shared/images/icons/file-icon.svg";
import PowerpointIcon from "_shared/images/icons/powerpoint-icon.svg";
import DocIcon from "_shared/images/icons/doc-icon.svg";
import ExcelIcon from "_shared/images/icons/excel-icon.svg";
import ImageIcon from "_shared/images/icons/image-icon.svg";
import { getEnv } from "../../utils/constants";
import { getBlobUploadEndpoint } from "./ResourcesAPI/Resources.api";
import Uppy from "@uppy/core";
const { BlobServiceClient, BlockBlobClient } = require("@azure/storage-blob");
const { getName } = require("country-list");

export const uppyDashboardClasses = `[&_.uppy-Dashboard-AddFiles-info]:block  
  [&_.uppy-Dashboard-AddFiles-info]:pb-[2px]
  [&_.uppy-Dashboard-AddFiles-info]:pt-0
  [&_.uppy-Dashboard-AddFiles-title]:mt-0
  [&_.uppy-Dashboard-AddFiles-title]:text-[14px]
  [&_.uppy-Dashboard-AddFiles-title]:font-normal
  [&_.uppy-Dashboard-AddFiles-title]:text-[rgba(55,61,67,0.72)]
  [&_.uppy-Dashboard-AddFiles]:border-0
  [&_.uppy-Dashboard-AddFiles]:before:mx-auto
  [&_.uppy-Dashboard-AddFiles]:before:block
  [&_.uppy-Dashboard-AddFiles]:before:h-14
  [&_.uppy-Dashboard-AddFiles]:before:w-16
  [&_.uppy-Dashboard-AddFiles]:before:bg-contain
  [&_.uppy-Dashboard-AddFiles]:before:bg-no-repeat
  [&_.uppy-Dashboard-AddFiles]:before:content-['']
  [&_.uppy-Dashboard-AddFiles]:before:bg-uppy-addfiles
  [&_.uppy-Dashboard-dropFilesHereHint]:hidden
  [&_.uppy-DashboardContent-back]:text-primary
  [&_.uppy-Dashboard-browse]:text-primary
  [&_.uppy-Dashboard-inner]:border-0
  [&_.uppy-Dashboard-inner]:bg-transparent
  [&_.uppy-Dashboard-note]:max-w-none
  [&_.uppy-Dashboard-note]:pt-0
  [&_.uppy-Dashboard-note]:text-[rgba(55,61,67,0.72)]
  [&_.uppy-Dashboard-poweredBy]:hidden
  [&_.uppy-Dashboard-Item]:border-0
  [&_.uppy-Informer]:hidden
`;

export const descriptionDefaultValues = {
  name: "",
  description: "",
  url: "",
  region: [],
  country: [],
  city: [],
  building: [],
  costCenter: [],
  teamMember: [],
  isActive: true,
};

export const hasSpecialChars = (name) => {
  return /[\/–]/.test(name);
};

export const cleanCategoryName = (category) => {
  const categoryName = category.replace(/\s+/gi, "-");
  const encodedCategory = encodeURIComponent(categoryName);

  const hasSpecialChar = hasSpecialChars(categoryName);
  return hasSpecialChar ? encodedCategory : categoryName;
};

export const calculateFileSize = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return 0;
  const sizeInMb = Number(bytes) / (1024 * 1024);
  return sizeInMb.toFixed(2);
};

export const getHighestOddAndEven = (items) => {
  let highestOdd = null;
  let highestEven = null;
  let highestOddIndex = -1;
  let highestEvenIndex = -1;

  items.forEach((item, index) => {
    const current = index + 1;
    if (current % 2 === 0) {
      if (current > highestEvenIndex) {
        highestEven = item;
        highestEvenIndex = current;
      }
    } else {
      if (current > highestOddIndex) {
        highestOdd = item;
        highestOddIndex = current;
      }
    }
  });

  const result = [];
  if (highestOdd?.key) result.push(String(highestOdd.key));
  if (highestEven?.key) result.push(String(highestEven.key));
  return result;
};

export const checkHasChild = (children) => {
  if (!children || children.length === 0) {
    return false;
  }

  return children.some((child) => child.isActive === true);
};

export const contentTypeDisplay = (contentType) => {
  switch (contentType) {
    case "resourceFile":
      return "File";
    case "resourceExternalLink":
      return "External Link";
    case "resourceSubpage":
      return "Subpage";
    case "resourceContactList":
      return "Contact List";
    default:
      return "N/A";
  }
};

export const contentTypeOptions = [
  { value: "resourceFile", label: "File" },
  { value: "resourceExternalLink", label: "External Link" },
  { value: "resourceContactList", label: "Contact List" },
  { value: "resourceSubpage", label: "Subpage" },
];

export const getDefaultContentTypeOption = (contentType) => {
  const contentTypeOptions = [
    { value: "resourceFile", label: "File" },
    { value: "resourceExternalLink", label: "External Link" },
    { value: "resourceContactList", label: "Contact List" },
    { value: "resourceSubpage", label: "Subpage" },
  ];
  return (
    contentTypeOptions.find((option) => option.value === contentType) || null
  );
};

export const resourceDefaultValues = {
  url: "",
  externalLink: "",
  subpageFilePath: "",
  name: "",
  description: "",
  region: [],
  country: [],
  city: [],
  building: [],
  costCenter: [],
  teamMember: [],
  activation: null,
  expiration: null,
  type: null,
  activationRadio: "Immediately",
  expirationRadio: "No Due Date",
  isSpecialPermission: false,
  isActive: true,
  fileName: "",
  fileSize: 0,
  inheritPermissions: true,
};

export const pdfUppyConfig = {
  autoProceed: true,
  restrictions: {
    maxNumberOfFiles: 1,
    allowedFileTypes: [
      ".pdf",
      ".docx",
      ".xlsx",
      ".xlsm",
      ".docm",
      ".pptm",
      ".xls",
      ".ppt",
      ".pptx",
      ".jpg",
      ".jpeg",
      ".png",
      ".zip",
    ],
    // maxFileSize: 105 * 1024 * 1024,
  },
};

export const imageUppyConfig = {
  autoProceed: true,
  restrictions: {
    maxNumberOfFiles: 1,
    allowedFileTypes: [".jpg", ".jpeg", ".png"],
    maxFileSize: 10 * 1024 * 1024,
  },
};

export const getDefaultLocation = (section, subSection, resourceInfo = {}) => {
  return subSection
    ? subSection.value !== "---"
      ? subSection.key || subSection.value
      : section.key || section.value
    : section
    ? section.key || section.value
    : resourceInfo?.key;
};

export const getIcon = (fileName) => {
  const extension = fileName?.split(".").pop()?.toLowerCase();
  if (extension === "doc" || extension === "docx") {
    return DocIcon;
  } else if (extension === "xls" || extension === "xlsx") {
    return ExcelIcon;
  } else if (extension === "ppt" || extension === "pptx") {
    return PowerpointIcon;
  } else if (
    extension === "jpg" ||
    extension === "jpeg" ||
    extension === "png"
  ) {
    return ImageIcon;
  } else {
    return FileIcon;
  }
};

export const filteredResources = (resources, filterType) => {
  if (!Array.isArray(resources)) return [];
  if (filterType === "Active Resources") {
    return resources.filter((r) => {
      if (r.expiration === "0001-01-01T00:00:00") return true;
      const expiryDate = new Date(r.expiration);
      expiryDate.setHours(0, 0, 0, 0);

      return expiryDate >= Date.now();
    });
  } else if (filterType === "Expired Resources") {
    return resources.filter((r) => {
      if (r.expiration === "0001-01-01T00:00:00") return false;
      const expiryDate = new Date(r.expiration);
      expiryDate.setHours(0, 0, 0, 0);

      return expiryDate < Date.now();
    });
  } else {
    return resources;
  }
};

//so this will filter the sections, subsections, and resources based on the filter
export const useFilterResources = (selectedResource, filterType) => {
  const processedSections = useMemo(() => {
    if (!selectedResource?.section) return [];

    const isExpiredFilter = filterType === "Expired Resources";

    return selectedResource.section
      .map((section) => {
        const rawResources = section.resources || [];
        const filteredRes = filteredResources(rawResources, filterType);

        const visibleSubSections = (section.subSections || [])
          .map((sub) => {
            const subRawRes = sub.resources || [];
            const subFiltered = filteredResources(subRawRes, filterType);

            const shouldShowSub = isExpiredFilter
              ? subFiltered.length > 0
              : subFiltered.length > 0 || subRawRes.length === 0;

            return { ...sub, filteredResources: subFiltered, shouldShowSub };
          })
          .filter((sub) => sub.shouldShowSub);

        const hasMatches =
          filteredRes.length > 0 || visibleSubSections.length > 0;
        const wasOriginallyEmpty =
          rawResources.length === 0 && (section.subSections || []).length === 0;

        const shouldShowSection = isExpiredFilter
          ? hasMatches
          : hasMatches || wasOriginallyEmpty;

        return {
          ...section,
          visibleResources: filteredRes,
          visibleSubSections,
          shouldShowSection,
        };
      })
      .filter((section) => section.shouldShowSection);
  }, [selectedResource, filterType]);

  return { processedSections };
};

export const loadTeamMemberOptions = _.debounce((input, callback) => {
  if (!input.trim()) {
    callback([]);
    return;
  }

  (async () => {
    const { data } = await get({
      url: `${getEnv("RESOURCES_BASE_URL")}/team-member/search/${input.trim()}`,
    });

    const options = data?.value
      ?.map((el) => ({
        value: el.teamMemberId,
        displayText: el.displayName,
        email: el.email,
        description: el.positionTitle,
        label: `${el.displayName} (${el.teamMemberId})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    callback(options);
  })();
}, 400);

export const loadCountriesOptions = _.debounce((input, callback) => {
  (async () => {
    const { data } = await get({
      url: `${getEnv(
        "RESOURCES_BASE_URL"
      )}/resourcepage/AvaliableCountryAndRegion`,
    });

    const options = data.countries
      .filter(
        (el) =>
          !input.trim() ||
          getName(el)?.toLowerCase().includes(input.toLowerCase().trim())
      )
      .map((el) => ({
        value: el,
        label: getName(el),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    callback(options);
  })();
}, 400);

export const loadRegionsOptions = _.debounce((input, callback) => {
  (async () => {
    const { data } = await get({
      url: `${getEnv(
        "RESOURCES_BASE_URL"
      )}/resourcepage/AvaliableCountryAndRegion`,
    });

    const options = data.regions
      .filter(
        (el) =>
          !input.trim() || el.toLowerCase().includes(input.toLowerCase().trim())
      )
      .map((el) => ({
        value: el,
        label: el,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    callback(options);
  })();
}, 400);

export const loadCostCentersOptions = _.debounce((input, callback) => {
  (async () => {
    const { data } = await get({
      url: `${getEnv("RESOURCES_BASE_URL")}/resourcepage/CostCenters`,
    });

    const options = data
      .filter((el) => !input.trim() || el.toString().includes(input))
      .map((el) => ({
        value: el,
        label: el,
      }));

    callback(options);
  })();
}, 400);

export const sortByReorderSubmitHandler = ({
  mutateSortByReorder,
  setIsLoading,
  selectedResource,
}) => {
  return async (data) => {
    setIsLoading(true);
    const keys = data.order.map((item) => item.key);

    const reorderPayload = {
      parentSectionId: data.parentSectionId,
      payload: keys,
    };

    try {
      await mutateSortByReorder({
        ...reorderPayload,
        departmentId: selectedResource?.key,
      });
    } catch (error) {
      setIsLoading(false);
    }
  };
};

//the sectionsubmithandler and resourcesubmithandler are for edit and create

export const sectionSubmitHandler = ({
  mode,
  form,
  mutateAddSection,
  mutateEditSection,
  mutateEditDepartment,
  setOpenAddDrawer,
  setLocationState,
  setActiveTab,
  setIsLoading,
  slideOutDetails,
  selectedResource,
}) => {
  const { addToast } = useToasts();
  return async (data) => {
    setIsLoading(true);
    const sendValues = (fields, data) =>
      fields.reduce((acc, key) => {
        acc[key] = data[key]?.map((opt) => opt.value) || [];
        return acc;
      }, {});

    const isSpecialPermission = () => {
      if (
        data.region?.length > 0 ||
        data.country?.length > 0 ||
        data.costCenter?.length > 0 ||
        data.teamMember?.length > 0 ||
        data.salesOnly === "yes" ||
        data.managerOnly === "yes"
      ) {
        return true;
      } else {
        return false;
      }
    };

    const payload = {
      isActive: data.isActive,
      sectionName: data.name,
      parentSectionId: data.parentSectionId,
      description: data.description,
      permission: {
        ...sendValues(["region", "country", "costCenter", "teamMember"], data),
        isSpecialPermission: isSpecialPermission(),
        salesOnly: data.salesOnly === "no" ? false : true,
        managerOnly: data.managerOnly === "no" ? false : true,
      },
      createdBy: `${user.firstName} ${user.lastName}`,
      isHidden: data.isHidden === "yes" ? true : false,
    };

    if (mode === "create") {
      setLocationState?.(payload.parentSectionId);
    }

    if (data.url) {
      if (typeof data.url === "string") {
        payload.url = data.url;
        payload.fileName = slideOutDetails?.fileName;
        payload.fileSize = slideOutDetails?.fileSize;
      } else {
        payload.fileName = data.url.name;
        payload.fileSize = data.url.size;
        payload.url = `${selectedResource?.key}/${payload.fileName}`;
      }
    }

    if (mode === "edit") {
      payload.sectionId = slideOutDetails?.key;
    }

    if (data.url && typeof data.url === "object") {
      let uploadEndpoint;
      try {
        uploadEndpoint = await getBlobUploadEndpoint({
          departmentId: selectedResource.key,
          directory: selectedResource?.key,
          fileName: data.url.name,
        });
      } catch (err) {
        setIsLoading(false);
        addToast("Error fetching Azure Blob upload endpoint.", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
        return;
      }
      try {
        const blockBlobClient = new BlockBlobClient(uploadEndpoint);
        const res = await blockBlobClient.uploadData(data.url, {
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
        });
      } catch (err) {
        setIsLoading(false);
        addToast("Error occured uploading to Azure Blob Storage.", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
        return;
      }
    }

    try {
      if (mode === "create") {
        await mutateAddSection({
          payload,
          departmentId: selectedResource?.key,
        });
      } else {
        if (slideOutDetails?.key === selectedResource?.key) {
          const deptPayload = {
            name: data.name,
            key: slideOutDetails.key,
            parentSectionId: slideOutDetails.parentSectionId,
            description: data.description,
            extendedInformation: { Description: data.description },
            permission: {
              ...sendValues(
                ["region", "country", "costCenter", "teamMember"],
                data
              ),
              isSpecialPermission: isSpecialPermission(),
              salesOnly: data.salesOnly === "no" ? false : true,
              managerOnly: data.managerOnly === "no" ? false : true,
            },
          };

          await mutateEditDepartment({
            payload: deptPayload,
            departmentId: selectedResource?.key,
          });
        } else {
          await mutateEditSection({
            payload,
            departmentId: selectedResource?.key,
          });
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };
};

export const resourceSubmitHandler = ({
  mode,
  form,
  mutateAddResource,
  mutateEditResource,
  mutateAddContactList,
  mutateEditContactList,
  setOpenAddDrawer,
  setLocationState,
  setActiveTab,
  setIsLoading,
  slideOutDetails,
  selectedResource,
}) => {
  const { addToast } = useToasts();
  return async (data) => {
    setIsLoading(true);
    const sendValues = (fields, data) =>
      fields.reduce((acc, key) => {
        acc[key] = data[key]?.map((opt) => opt.value) || [];
        return acc;
      }, {});

    const isSpecialPermission = () => {
      if (
        data.region.length > 0 ||
        data.country.length > 0 ||
        data.costCenter.length > 0 ||
        data.teamMember.length > 0 ||
        data.salesOnly === "yes" ||
        data.managerOnly === "yes"
      ) {
        return true;
      } else {
        return false;
      }
    };

    const payload = {
      activationDate: data.activation || new Date().toISOString(),
      expirationDate: data.expiration,
      resourceName: data.name,
      parentSectionId: data.parentSectionId,
      description: data.description,
      forceDownload: data.forceDownload === "no" ? false : true,
      resourceDocumentType:
        data.type?.value.charAt(0).toUpperCase() + data.type?.value.slice(1),
      permission: {
        ...sendValues(["region", "country", "costCenter", "teamMember"], data),
        isSpecialPermission: isSpecialPermission(),
        salesOnly: data.salesOnly === "no" ? false : true,
        managerOnly: data.managerOnly === "no" ? false : true,
      },
    };

    const hasSectionErrors = () => {
      let hasSectionErrors = false;

      data.contactSection.forEach((section, index) => {
        const items = section.resourceContactItems || [];

        const deletedContacts = items.every((item) => item.isActive === false);

        if (items.length === 0) {
          form.setError(`contactSection.${index}`, {
            type: "manual",
            message: "At least one contact is required for this section.",
          });
          hasSectionErrors = true;
        }

        if (items.length > 0 && deletedContacts) {
          form.setError(`contactSection.${index}`, {
            type: "manual",
            message: "At least one contact is required for this section.",
          });
          hasSectionErrors = true;
        }
      });

      return hasSectionErrors;
    };

    if (data.type?.value === "resourceContactList" && hasSectionErrors()) {
      setIsLoading(false);
      setActiveTab("Details");
      return;
    }

    if (mode === "create") {
      setLocationState?.(payload.parentSectionId);
    }

    delete payload.activationRadio;
    delete payload.expirationRadio;
    delete payload.type;

    if (data.type.value === "resourceSubpage") {
      payload.url = data.subpageFilePath?.trim();
    }

    if (data.type.value === "resourceExternalLink") {
      payload.url = data.externalLink;
    }

    if (data.type?.value === "resourceFile" && data.url) {
      if (typeof data.url === "string") {
        payload.url = data.url;
        payload.fileName = slideOutDetails?.fileName;
        payload.fileSize = slideOutDetails?.fileSize;
      } else {
        payload.fileName = data.url.name;
        payload.fileSize = data.url.size;
        payload.url = `${selectedResource?.key}/${payload.fileName}`;
      }
    }

    if (data.type?.value === "resourceContactList") {
      delete payload.description;
      payload.contactSections = data.contactSection;
    }

    if (mode === "edit") {
      payload.resourceId = slideOutDetails?.key;
    }

    if (data.type?.value === "resourceFile" && typeof data.url === "object") {
      let uploadEndpoint;
      try {
        uploadEndpoint = await getBlobUploadEndpoint({
          departmentId: selectedResource.key,
          directory: selectedResource?.key,
          fileName: data.url.name,
        });
      } catch (err) {
        setIsLoading(false);
        addToast("Error fetching Azure Blob upload endpoint.", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
        return;
      }

      try {
        const blockBlobClient = new BlockBlobClient(uploadEndpoint);
        const res = await blockBlobClient.uploadData(data.url, {
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
        });
      } catch (err) {
        setIsLoading(false);
        addToast("Error occured uploading to Azure Blob Storage.", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
        return;
      }
    }

    console.log("THIS IS PAYLOAD RESOURCE SUBMIT---", payload);

    try {
      if (mode === "create") {
        if (data.type?.value === "resourceContactList") {
          await mutateAddContactList({
            payload,
            departmentId: selectedResource?.key,
          });
        } else {
          await mutateAddResource({
            payload,
            departmentId: selectedResource?.key,
          });
        }
      } else {
        if (data?.type?.value === "resourceContactList") {
          await mutateEditContactList({
            payload,
            departmentId: selectedResource?.key,
          });
        } else {
          await mutateEditResource({
            payload,
            departmentId: selectedResource?.key,
          });
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };
};
